<template>
  <div>
    <h4>{{ $t('jobs.products-by-supplier') }}:</h4>
    <div
      v-for="{ id, jobApiId, name, products } of productsBySupplier"
      :key="id"
      class="ml-1"
    >
      <h5 class="mt-3 mb-2">{{ name }}</h5>

      <template v-if="products.length">
        <ul
          v-for="(product, index) of products"
          :key="index"
          class="bg-transparent list-unstyled"
        >
          <li class="d-flex align-items-baseline">
            <span class="mr-1">{{ product.quantity }}&nbsp;&times;</span>
            <div>
              <span>{{ product.name }}</span>
              <span
                v-if="!getCurrentProductOrder(jobApiId, id)"
                v-b-tooltip.hover
                :title="$t('products-order.missing-tracking-info')"
              >
                <BIconExclamationTriangle
                  class="ml-2"
                  style="font-size: 1.7rem"
                  variant="danger"
                />
              </span>
              <div v-if="hasMultiValue(product.serial_number)">
                SN: {{ formatMultiValue(product.serial_number) }}
              </div>
              <template
                v-if="
                  hasMultiValue(product.chargepoint_number) ||
                  chargepointRequired(product)
                "
              >
                | Chargepoint:
                <template v-if="hasMultiValue(product.chargepoint_number)">
                  {{ formatMultiValue(product.chargepoint_number) }}
                  <b-button
                    size="sm"
                    title="Edit"
                    class="align-baseline"
                    v-b-tooltip.hover
                    @click="editChargepoint(product)"
                  >
                    <BIconPencil />
                  </b-button>
                </template>
                <b-button
                  v-else
                  variant="warning"
                  size="sm"
                  class="align-baseline"
                  @click="editChargepoint(product)"
                >
                  Enter
                </b-button>
              </template>
              <template
                v-if="
                  hasMultiValue(product.product_password) ||
                  passwordRequired(product)
                "
              >
                | Password:
                <template v-if="hasMultiValue(product.product_password)">
                  {{ formatMultiValue(product.product_password) }}
                  <b-button
                    size="sm"
                    title="Edit"
                    class="align-baseline"
                    v-b-tooltip.hover
                    @click="editPassword(product)"
                  >
                    <BIconPencil />
                  </b-button>
                </template>
                <b-button
                  v-else
                  variant="warning"
                  size="sm"
                  class="align-baseline"
                  @click="editPassword(product)"
                >
                  Enter
                </b-button>
              </template>
            </div>
          </li>
        </ul>
      </template>
      <template v-if="getCurrentProductOrder(jobApiId, id)">
        <div class="mb-1">
          <b-icon-box class="mr-2" />
          <a :href="getCurrentProductOrder(jobApiId, id)?.url" target="_blank">
            {{ $t('products-order.tracking-link') }}
          </a>
        </div>
        <div class="mb-2">
          {{ $t('products-order.shipping-to') }}:
          <b-badge variant="secondary">
            {{
              $t(
                `products-order.${
                  getCurrentProductOrder(jobApiId, id)?.shipTo
                }`,
              )
            }}
          </b-badge>
        </div>
      </template>

      <b-button
        v-if="canUpdateTrackingID"
        :variant="getCurrentProductOrder(jobApiId, id) ? 'primary' : 'danger'"
        class="mb-4"
        @click="$emit('toggleModal', { id, jobApiId, name, products })"
      >
        {{
          $t(
            `products-order.${
              getCurrentProductOrder(jobApiId, id) ? 'update' : 'add'
            }-tracking-info`,
          )
        }}
      </b-button>
    </div>
    <ChargepointModal ref="chargepointModal" @updated="chargepointUpdated" />
    <PasswordModal ref="passwordModal" @updated="passwordUpdated" />
  </div>
</template>

<script>
import { BIconBox, BIconExclamationTriangle, BIconPencil } from 'bootstrap-vue';
import { JobStatusEnum } from '@gid/models';
import { StatusRulesNamesEnum } from '@gid/models/dist/entities/status-rules-names-enum';
import axios from 'axios';
import { parseMultiValueField } from '@gid/vue-common/utils';
import ChargepointModal from './ChargepointModal.vue';
import PasswordModal from './PasswordModal.vue';

export default {
  components: {
    BIconBox,
    BIconExclamationTriangle,
    BIconPencil,
    ChargepointModal,
    PasswordModal,
  },
  props: {
    item: Object,
    getCurrentProductOrder: Function,
    toggleModal: Function,
  },
  data() {
    return {
      jobProductsRequiringChargepoint: [],
      jobProductsRequiringPassword: [],
      productIdToJobProductId: {},
    };
  },
  computed: {
    canUpdateTrackingID() {
      const state = `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.BRAND_PRODUCT_ORDERING}.waiting`;
      const states = this.item?.job?.states;
      return (
        states?.list?.includes(state) ||
        (Array.isArray(states) && states.includes(state)) ||
        this.item?.jobs?.some((job) => job.job.states?.includes(state))
      );
    },
    productsBySupplier() {
      const bySupplier = {};

      this.item.products.forEach((product) => {
        const { supplier_sfid, supplier_name, job_api_id } = product;

        if (!bySupplier[supplier_sfid]) {
          bySupplier[supplier_sfid] = {
            id: supplier_sfid,
            jobApiId: job_api_id,
            name: supplier_name,
            products: [],
          };
        }

        bySupplier[supplier_sfid].products.push(product);
      });

      return Object.values(bySupplier);
    },
  },
  async created() {
    if (
      this.item.job.states?.list.includes(
        `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.CHARGEPOINT_REQUIRED}.missing`,
      ) ||
      this.item.products.some((p) => p.chargepoint_number) ||
      this.item.job.states?.list.includes(
        `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.PASSWORD_REQUIRED}.missing`,
      ) ||
      this.item.products.some((p) => p.product_password)
    ) {
      try {
        const { data: rulesDetails } = await axios.get(
          `/jobs-api/status-rules/job/${this.item.job.api_id}/rules-details`,
        );
        this.jobProductsRequiringChargepoint =
          rulesDetails[StatusRulesNamesEnum.CHARGEPOINT_REQUIRED]
            ?.jobProductsRequiringChargepoint ?? [];
        this.jobProductsRequiringPassword =
          rulesDetails[StatusRulesNamesEnum.PASSWORD_REQUIRED]
            ?.jobProductsRequiringPassword ?? [];

        // Create mappings from product ID to job_product ID
        [
          ...this.jobProductsRequiringChargepoint,
          ...this.jobProductsRequiringPassword,
        ].forEach((jp) => {
          this.productIdToJobProductId[jp.productId] = jp.id;
        });
      } catch (error) {
        // Keep UI usable even if rules-details is temporarily unavailable
        // (buttons might not show requirements, but existing values still display).
        console.error('Error fetching rules details:', error);
      }
    }

    // Fetch all job products to ensure complete mapping for updates.
    try {
      const { data: allJobProducts } = await axios.get(
        `/data-api/job-product/opportunity/${this.item.opportunity.sfid}/job/${this.item.job.api_id}`,
      );
      allJobProducts.forEach((jp) => {
        if (jp.product_id && jp.id) {
          this.productIdToJobProductId[jp.product_id] = jp.id;
        }
      });
    } catch (error) {
      console.error('Error fetching job products:', error);
    }
  },
  methods: {
    formatMultiValue(value) {
      const values = parseMultiValueField(value);
      if (!values.length) return '';
      return values.join(', ');
    },
    hasMultiValue(value) {
      return parseMultiValueField(value).length > 0;
    },
    chargepointRequired(jp) {
      return this.jobProductsRequiringChargepoint.find(
        (item) => item.productId == jp.id,
      );
    },
    editChargepoint(product) {
      let jp = this.chargepointRequired(product);
      if (!jp && this.productIdToJobProductId[product.id]) {
        jp = { id: this.productIdToJobProductId[product.id] };
      }
      this.$refs.chargepointModal.open(product, jp);
    },
    chargepointUpdated(product) {
      this.item.products.find((p) => p.id == product.id).chargepoint_number =
        product.chargepoint_number;
      setTimeout(() => {
        this.$emit('refresh');
      }, 2000);
    },
    passwordRequired(jp) {
      return this.jobProductsRequiringPassword.find(
        (item) => item.productId == jp.id,
      );
    },
    editPassword(product) {
      let jp = this.passwordRequired(product);
      if (!jp && this.productIdToJobProductId[product.id]) {
        jp = { id: this.productIdToJobProductId[product.id] };
      }
      this.$refs.passwordModal.open(product, jp);
    },
    async passwordUpdated(product) {
      this.item.products.find((p) => p.id == product.id).product_password =
        product.product_password;
      this.confirmedPassword();
    },
    confirmedPassword() {
      const event = 'PASSWORD_FILLED';
      axios
        .post(
          `/jobs-api/status-rules/job/${this.item.job.api_id}/apply-event`,
          {
            type: event,
          },
        )
        .then(() => {
          this.$emit('refresh');
        });
    },
  },
};
</script>
